import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataFallback from '../components/feedback/DataFallback';

export default function BrandProductsPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [downloadingImages, setDownloadingImages] = useState(new Set());

  // Function to download image
  const downloadImage = async (imageUrl, productTitle, productId) => {
    const downloadKey = `${productId}_single`;
    setDownloadingImages(prev => new Set([...prev, downloadKey]));
    
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${productTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_image.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open image in new tab
      window.open(imageUrl, '_blank');
    } finally {
      setDownloadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  // Function to download all images for a product
  const downloadAllImages = async (product) => {
    if (!product.image_links || product.image_links.length === 0) return;
    
    const downloadKey = `${product.id}_all`;
    setDownloadingImages(prev => new Set([...prev, downloadKey]));
    
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    try {
      for (let i = 0; i < product.image_links.length; i++) {
        const imageUrl = product.image_links[i];
        const filename = `${product.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_image_${i + 1}`;
        
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          // Add small delay between downloads to avoid overwhelming the browser
          if (i < product.image_links.length - 1) {
            await delay(500);
          }
        } catch (error) {
          console.error(`Error downloading image ${i + 1}:`, error);
        }
      }
    } finally {
      setDownloadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    
    fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${brandId}/products/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
        return res.json();
      })
      .then(data => {
        console.log('Products API Response:', data);
        // Handle both response structures: direct array or {products: [...]}
        const productsArray = Array.isArray(data) ? data : (data.products || []);
        setProducts(productsArray);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [brandId]);

  const filtered = products.filter(p =>
    (p.title && p.title.toLowerCase().includes(search.toLowerCase())) ||
    (p.tags && p.tags.toLowerCase().includes(search.toLowerCase())) ||
    (p.product_type && p.product_type.toLowerCase().includes(search.toLowerCase())) ||
    (p.vendor && p.vendor.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div style={{padding: '2rem', textAlign: 'center'}}>Loading products...</div>;
  if (error) return (
    <div style={{ padding: '2rem' }}>
      <DataFallback
        variant="pane"
        error={typeof error === 'string' ? { message: error, retryable: true } : error}
        onRetry={() => window.location.reload()}
      />
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{maxWidth:1000,margin:'3rem auto',background:'#fff',borderRadius:16,padding:'2.5rem 2rem',boxShadow:'0 2px 16px rgba(60,60,90,0.10)'}}>
        <button onClick={() => navigate(-1)} style={{marginBottom:'1.5rem',background:'#e6f4f1',color:'#004443',border:'none',borderRadius:6,padding:'0.5rem 1.2rem',fontWeight:600,cursor:'pointer'}}>← Back</button>
        <h1 style={{marginBottom:'1.2rem'}}>Brand Products & Data</h1>
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{width:'100%',padding:'0.75rem 1rem',border:'1px solid #ddd',borderRadius:8,marginBottom:'1.5rem',fontSize:'1rem'}}
        />
        {filtered.length === 0 ? (
          <div style={{textAlign:'center',color:'#888',padding:'2rem'}}>No products found.</div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'1.5rem'}}>
            {filtered.map(product => (
              <div key={product.id} style={{border:'1px solid #e0e0e0',borderRadius:12,padding:'1.2rem',background:'#fafafa',position:'relative'}}>
                {/* Product Image */}
                {product.image_links && product.image_links.length > 0 && (
                  <div style={{marginBottom:'1rem',position:'relative'}}>
                    <img 
                      src={product.image_links[0]} 
                      alt={product.images?.[0]?.alt || product.title}
                      style={{
                        width:'100%',
                        height:'200px',
                        objectFit:'cover',
                        borderRadius:'8px',
                        background:'#f5f5f5'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Download button overlay */}
                    <button
                      onClick={() => downloadImage(product.image_links[0], product.title, product.id)}
                      disabled={downloadingImages.has(`${product.id}_single`)}
                      style={{
                        position:'absolute',
                        top:'8px',
                        right:'8px',
                        background: downloadingImages.has(`${product.id}_single`) ? 'rgba(0,191,174,0.8)' : 'rgba(0,0,0,0.7)',
                        color:'white',
                        border:'none',
                        borderRadius:'6px',
                        padding:'6px 8px',
                        fontSize:'0.8rem',
                        cursor: downloadingImages.has(`${product.id}_single`) ? 'not-allowed' : 'pointer',
                        display:'flex',
                        alignItems:'center',
                        gap:'4px'
                      }}
                      title="Download Image"
                    >
                      {downloadingImages.has(`${product.id}_single`) ? (
                        <>
                          <div style={{width:'14px',height:'14px',border:'2px solid #fff',borderTop:'2px solid transparent',borderRadius:'50%',animation:'spin 1s linear infinite'}}></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                          </svg>
                          Download
                        </>
                      )}
                    </button>
                  </div>
                )}
                
                <h3 style={{marginBottom:'0.5rem',color:'#004443'}}>{product.title || 'Product'}</h3>
                <div style={{color:'#666',fontSize:'0.85rem',marginBottom:'0.5rem'}}>
                  <strong>Type:</strong> {product.product_type || 'N/A'}
                </div>
                <div style={{color:'#666',fontSize:'0.85rem',marginBottom:'0.5rem'}}>
                  <strong>Vendor:</strong> {product.vendor || 'N/A'}
                </div>
                <div style={{color:'#666',fontSize:'0.85rem',marginBottom:'0.5rem'}}>
                  <strong>Status:</strong> <span style={{color: product.status === 'active' ? '#00bfae' : '#888'}}>{product.status || 'N/A'}</span>
                </div>
                <div style={{color:'#666',fontSize:'0.85rem',marginBottom:'1rem'}}>
                  <strong>Tags:</strong> {product.tags || 'No tags'}
                </div>
                <div style={{color:'#888',fontSize:'0.8rem',marginBottom:'0.5rem'}}>
                  Created: {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                </div>
                
                {/* Additional image info and bulk download */}
                {product.images && product.images.length > 0 && (
                  <div style={{color:'#888',fontSize:'0.75rem',marginTop:'0.5rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span>{product.images.length} image{product.images.length > 1 ? 's' : ''} • {product.images[0].width}x{product.images[0].height}px</span>
                    {product.images.length > 1 && (
                      <button
                        onClick={() => downloadAllImages(product)}
                        disabled={downloadingImages.has(`${product.id}_all`)}
                        style={{
                          background: downloadingImages.has(`${product.id}_all`) ? '#ccc' : '#00bfae',
                          color:'white',
                          border:'none',
                          borderRadius:'4px',
                          padding:'4px 8px',
                          fontSize:'0.7rem',
                          cursor: downloadingImages.has(`${product.id}_all`) ? 'not-allowed' : 'pointer',
                          display:'flex',
                          alignItems:'center',
                          gap:'2px'
                        }}
                        title="Download All Images"
                      >
                        {downloadingImages.has(`${product.id}_all`) ? (
                          <>
                            <div style={{width:'10px',height:'10px',border:'1px solid #fff',borderTop:'1px solid transparent',borderRadius:'50%',animation:'spin 1s linear infinite'}}></div>
                            Downloading...
                          </>
                        ) : (
                          `Download All (${product.images.length})`
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
